// The upload queue behind "Save N files": one request per row file, at most
// `concurrency` at a time, started in the order they were added. Pure (no
// React; useStagingQueue wraps it), unit-tested with deferred promises.
//
// Item states:
//   queued      waiting for a free slot
//   uploading   bytes going up (progress 0..1)
//   processing  every byte sent, the server is storing it (media-service may
//               convert a Word file to PDF, which takes seconds)
//   saved       the server answered 200
//   failed      it answered an error or the network failed (error.retryable)
//   not_sent    never started: an earlier answer stopped the queue (fatal)

const ACTIVE = new Set(['queued', 'uploading', 'processing'])

/** Whether an item is still to be sent or on its way. */
export const isActive = (item) => ACTIVE.has(item?.state)

/**
 * @param {object} opts
 * @param {number} [opts.concurrency=3]
 * @param {(item, onProgress: (fraction:number) => void) => Promise<any>} opts.upload
 *   sends one item, resolves with the server's data
 * @param {(err) => { message: string, retryable: boolean, fatal?: boolean }} opts.classify
 *   what a failure means (errors.stageError)
 * @param {(items: object[], info: { progress: boolean }) => void} [opts.onChange]
 *   after every change, a copy of every item; info.progress: only an upload's
 *   progress moved (a 5% step), no item changed state
 * @param {(item, data) => void} [opts.onSaved]        one item saved, with the server's data
 * @param {(failure, item) => void} [opts.onFatal]     the queue stopped on this failure
 * @param {(summary: { saved: number, failed: number, fatal: object|null }) => void} [opts.onDrain]
 *   nothing left to send or in flight; counts since the queue last went busy
 */
export function createUploadQueue({
  concurrency = 3,
  upload,
  classify,
  onChange = () => {},
  onSaved = () => {},
  onFatal = () => {},
  onDrain = () => {},
}) {
  const items = new Map() // id -> item; insertion order = start order
  let running = 0
  let stopped = false
  let run = { saved: 0, failed: 0, fatal: null }

  const snapshot = () => [...items.values()].map((item) => ({ ...item }))
  const emit = (progress = false) => onChange(snapshot(), { progress })
  const busy = () => [...items.values()].some(isActive)

  const settle = () => {
    running -= 1
    pump()
    emit()
    if (running === 0 && !busy()) {
      const summary = run
      run = { saved: 0, failed: 0, fatal: null }
      onDrain(summary)
    }
  }

  function start(item) {
    running += 1
    item.state = 'uploading'
    item.progress = 0
    item.error = null
    const onProgress = (fraction) => {
      if (item.state !== 'uploading') return
      const value = Math.max(0, Math.min(1, Number(fraction) || 0))
      // Listeners hear 5% steps, not every progress event of the browser.
      const step = Math.floor(value * 20) !== Math.floor(item.progress * 20)
      item.progress = value
      if (value >= 1) item.state = 'processing'
      if (value >= 1) emit()
      else if (step) emit(true)
    }
    Promise.resolve()
      .then(() => upload(item, onProgress))
      .then(
        (data) => {
          item.state = 'saved'
          item.progress = 1
          run.saved += 1
          try {
            onSaved({ ...item }, data)
          } catch {
            // a listener's error never stalls the queue
          }
        },
        (err) => {
          const failure = classify ? classify(err) : { message: 'Upload failed.', retryable: true }
          item.state = 'failed'
          item.error = { message: failure.message, retryable: Boolean(failure.retryable), code: failure.code || null }
          run.failed += 1
          if (failure.fatal) {
            run.fatal = failure
            halt()
            try {
              onFatal(failure, { ...item })
            } catch {
              // see above
            }
          }
        }
      )
      .finally(settle)
  }

  function pump() {
    if (stopped) return
    for (const item of items.values()) {
      if (running >= concurrency) break
      if (item.state === 'queued') start(item)
    }
  }

  // Stop starting new uploads; the ones in flight finish.
  function halt() {
    stopped = true
    for (const item of items.values()) {
      if (item.state === 'queued') item.state = 'not_sent'
    }
  }

  function requeue(item) {
    item.state = 'queued'
    item.progress = 0
    item.error = null
  }

  return {
    /**
     * Queue items ({ id, ... } plus what `upload` needs). An id already in the
     * queue is replaced and moved to the end, unless it is still active.
     */
    add(newItems) {
      stopped = false
      for (const raw of newItems || []) {
        const current = items.get(raw.id)
        if (current && isActive(current)) continue
        items.delete(raw.id)
        items.set(raw.id, { ...raw, state: 'queued', progress: 0, error: null })
      }
      pump()
      emit()
    },
    /** Send one failed or not-sent item again. */
    retry(id) {
      const item = items.get(id)
      if (!item || !(item.state === 'failed' || item.state === 'not_sent')) return
      stopped = false
      requeue(item)
      pump()
      emit()
    },
    /** Send every retryable failure and every not-sent item again. */
    retryFailed() {
      let any = false
      for (const item of items.values()) {
        if (item.state === 'not_sent' || (item.state === 'failed' && item.error?.retryable)) {
          requeue(item)
          any = true
        }
      }
      if (!any) return
      stopped = false
      pump()
      emit()
    },
    /** Forget an item that is not active (saved, failed, not sent). */
    remove(id) {
      const item = items.get(id)
      if (!item || isActive(item)) return
      items.delete(id)
      emit()
    },
    /** Forget every saved item (the progress summary was read). */
    clearFinished() {
      let any = false
      for (const [id, item] of items) {
        if (item.state === 'saved') {
          items.delete(id)
          any = true
        }
      }
      if (any) emit()
    },
    /** Start nothing new (e.g. the page is left); uploads in flight finish. */
    stop() {
      halt()
      emit()
    },
    snapshot,
  }
}
